/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useState, useEffect } from "react";
import cn from "classnames";
import { LayoutContext } from "../../components/layout";
import fetchHelper from "../../services/fetchHelper";

const NotesFields = ({ title, inputName, disable = false }) => {
  const { headerDetails: userInfo, updateHeaderDetails } = useContext(LayoutContext);
  const [isLoading, setIsLoading] = useState(false);
  const [canEditNotes, setCanEditNotes] = useState(false);
  const [isDisable, setIsDisable] = useState(disable);
  const [notesSaveSuccess, setNotesSaveSuccess] = useState(null);
  const [additionalNotes, setAdditionalNotes] = useState(userInfo[inputName]);
  const [prevAdditionalNotes, setPrevAdditionalNotes] = useState("");
  const [additionalNotesError, setAdditionalNotesError] = useState(false);

  useEffect(() => {
    setIsDisable(disable);
  }, [disable]);

  const onSubmitAdditionalNotes = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setIsLoading(true);
    setAdditionalNotesError(false);
    try {
      const url = "/patientmanagement/rest/interrogations/" + userInfo.id;
      const data = {};
      data[inputName] = additionalNotes;
      data.id = userInfo.id;

      const response = await fetch(url, {
        method: "PUT",
        headers: fetchHelper.getSecurityHeader(),
        body: JSON.stringify(data),
      });
      const result = await response.json();
      userInfo[inputName] = result[inputName];
      updateHeaderDetails(userInfo);
      if (response.ok) {
        setNotesSaveSuccess(true);
        setCanEditNotes(false);
        setAdditionalNotesError(false);
      } else {
        setAdditionalNotesError(true);
        setNotesSaveSuccess(false);
      }
    } catch (err) {
      setAdditionalNotesError(true);
      setNotesSaveSuccess(false);
    } finally {
      setIsLoading(false);
    }
    setNotesSaveSuccess(null);
  };

  return (
    <>
      <div className="grp-hdr">
        <h3>{title}</h3>
      </div>
      <div className="ta-wrap">
        <textarea
          id={inputName === "additionalNotes" ? "additional-notes-text-field" : "provider-notes-text-field"}
          className={cn({ "editable-txtarea": canEditNotes }, { "border-red": additionalNotesError })}
          placeholder="Type notes here"
          disabled={!canEditNotes}
          value={additionalNotes}
          onChange={(e) => {
            setAdditionalNotes(e.target.value);
          }}
          style={{ minHeight: 240 }}
        />
        <div
          className={cn(
            "ta-btn-wrap",
            { "ta-btn-wrap-bordered": canEditNotes },
            { "border-red": additionalNotesError }
          )}
        >
          {!isDisable ? (
            <>
              {canEditNotes ? (
                <div
                  className="btn btn-sm"
                  id={
                    inputName === "additionalNotes"
                      ? "additional-notes-cancel-btn"
                      : "provider-notes-cancel-btn"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setAdditionalNotes(prevAdditionalNotes || "");
                    setCanEditNotes(!canEditNotes);
                    setAdditionalNotesError(false);
                  }}
                >
                  Cancel
                </div>
              ) : (
                <div
                  id={
                    inputName === "additionalNotes" ? "additional-notes-edit-btn" : "provider-notes-edit-btn"
                  }
                  className="btn btn-sm btn-teal"
                  onClick={(e) => {
                    e.preventDefault();
                    setPrevAdditionalNotes(additionalNotes);
                    setCanEditNotes(!canEditNotes);
                  }}
                >
                  {notesSaveSuccess === null ? (
                    "Edit"
                  ) : notesSaveSuccess ? (
                    <div
                      className="check-flat-ico icon-64"
                      style={{ height: 20, width: 20, marginTop: -5 }}
                    />
                  ) : (
                    ""
                  )}
                </div>
              )}
            </>
          ) : null}
          {canEditNotes && (
            <button
              className="btn btn-sm btn-teal"
              onClick={onSubmitAdditionalNotes}
              disabled={isLoading || !canEditNotes || additionalNotes === prevAdditionalNotes}
              data-id="edit-notes-btn"
              id={inputName === "additionalNotes" ? "additional-notes-save-btn" : "provider-notes-save-btn"}
            >
              {" "}
              {isLoading ? (
                <div
                  id="results-loading-icon"
                  className="saving-white-ico icon-64"
                  style={{ height: 20, width: 20 }}
                />
              ) : !notesSaveSuccess && notesSaveSuccess !== null ? (
                <div className="close-ico icon-64" style={{ height: 20, width: 20 }} />
              ) : (
                "Save"
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default NotesFields;
